import * as angular from 'angular';
import { Observable } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';

import { TooltipPlacement } from '@mode/capra';
import { CellExportFacade, NotebookTypes } from '@mode/notebooks/contract';

import { SessionResource } from '@mode/legacy/data-access-model-js';
import { NotebookState } from '@mode/legacy/types-app-state';
import { FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { getThoughtspotIntegrated } from '@mode/shared/util-js';
import { NotebookDataService } from '~/services/notebook-data-service';
import { MixableBaseController } from '../../../common/component';
import { NotebookStateService } from './notebook-state-service';

class NotebookToolbarController extends MixableBaseController {
  static $inject = ['NotebookDataService', 'NotebookStateService', 'FeatureFlagsFacade', 'CellExportFacade', '$window'];
  notebookToken: string;
  session: SessionResource;
  report: any;
  enableNotebookExportDatasets$: Observable<boolean>;
  readonly tooltipPlacement: TooltipPlacement = 'bottom';
  private cellType: 'code' | 'markdown'; // for the select dropdown

  private notebookState$: Observable<NotebookState>;

  private selectedNotebookToken$: Observable<string>;
  private cellToken$: Observable<string>;
  private nextCellToken$: Observable<string>;
  private prevCellToken$: Observable<string>;
  private cellExecuting$: Observable<boolean>;
  private contentType$: Observable<NotebookTypes.ContentType>;
  private isInLayout$: Observable<boolean>;
  private isEditing$: Observable<boolean>;
  private isFrozen$: Observable<boolean>;
  private isFolded$: Observable<boolean>;
  private isPython$: Observable<boolean>;
  private isMarkdown$: Observable<boolean>;

  private canRestartSession$: Observable<boolean>;
  private canDelete$: Observable<boolean>;
  private canFreeze$: Observable<boolean>;
  private canRunCell$: Observable<boolean>;

  private hasOutput$: Observable<boolean>;
  private hasResult$: Observable<boolean>;

  private canRun = false;
  private isCancelling = false;
  private isRunning = false;
  private runAllIcon = '';

  private hasNotebookContentLink$: Observable<boolean>;
  private notebookContentLink$: Observable<string>;

  private isSelectedCellExporting$: Observable<boolean>;

  private thoughtspotIntegrated: boolean;

  constructor(
    private notebookDataService: NotebookDataService,
    private notebookStateService: NotebookStateService,
    private featureFlagService: FeatureFlagsFacade,
    private cellExportFacade: CellExportFacade,
    private $window: angular.IWindowService
  ) {
    super();
    this.thoughtspotIntegrated = getThoughtspotIntegrated(this.$window.document);
  }

  $onInit() {
    this.selectedNotebookToken$ = this.notebookStateService.selectedNotebookToken$;
    this.notebookState$ = this.notebookStateService.notebookState$;

    this.cellToken$ = this.notebookStateService.cellToken$;
    this.cellExecuting$ = this.notebookStateService.cellExecuting$;
    this.contentType$ = this.notebookStateService.contentType$;
    this.isInLayout$ = this.notebookStateService.isInLayout$;
    this.nextCellToken$ = this.notebookStateService.nextCellToken$;
    this.prevCellToken$ = this.notebookStateService.prevCellToken$;

    this.isEditing$ = this.notebookStateService.isEditing$;
    this.isFolded$ = this.notebookStateService.isFolded$;
    this.isFrozen$ = this.notebookStateService.isFrozen$;
    this.isMarkdown$ = this.contentType$.pipe(map((c) => c === NotebookTypes.ContentType.Markdown));
    this.isPython$ = this.contentType$.pipe(map((c) => c === NotebookTypes.ContentType.Python));
    this.canDelete$ = this.notebookStateService.canDelete$;
    this.canFreeze$ = this.notebookStateService.canFreeze$;
    this.canRunCell$ = this.notebookStateService.canRunCell$(this.notebookToken);
    this.canRestartSession$ = this.notebookStateService.canRestartSession$;

    this.notebookState$.subscribe((state) => {
      this.isCancelling = state === NotebookState.Cancelling;
      this.isRunning = state === NotebookState.Running;
      this.canRun = state === NotebookState.Ready || state === NotebookState.Cancelled;

      this.runAllIcon = this.isCancelling ? '' : this.isRunning ? 'icon-stop' : 'icon-play';
    });

    this.hasOutput$ = this.notebookDataService.hasOutput$;
    this.hasResult$ = this.notebookStateService.hasResult$;

    this.contentType$
      .pipe(
        takeUntil(this.destroyed$),
        map((t) => (t === NotebookTypes.ContentType.Markdown ? 'markdown' : 'code'))
      )
      .subscribe((type) => (this.cellType = type));

    this.hasNotebookContentLink$ = this.notebookStateService.notebookResource$.pipe(map((r) => r.hasLink('export')));
    this.notebookContentLink$ = this.notebookStateService.notebookResource$.pipe(
      map((r) => r.links('export').$expand({ markdown_comments: true, cell_headers: false }))
    );
    this.enableNotebookExportDatasets$ = this.featureFlagService
      .asObservable(FeatureFlag.NotebookExportDatasets)
      .pipe(takeUntil(this.destroyed$));

    this.isSelectedCellExporting$ = this.cellToken$.pipe(
      switchMap((cellToken) => this.cellExportFacade.isExportingByCell$(cellToken))
    );
  }

  delete(cellToken: string) {
    if (cellToken) {
      this.notebookDataService.removeCell(this.notebookToken, cellToken);
    }
  }

  runCell(params: { cellToken: string; contentType: NotebookTypes.ContentType; nextCellToken: string }) {
    const { cellToken, contentType, nextCellToken } = params;
    // currently rely on blur event to save markdown
    if (contentType !== NotebookTypes.ContentType.Markdown) {
      this.notebookDataService.runCell(this.notebookToken, cellToken);
    }

    // TODO: Consider a Run cell and select next action
    if (!nextCellToken) {
      this.notebookDataService.addCell(this.notebookToken);
    } else {
      this.notebookDataService.selectCell(nextCellToken, false);
    }
  }

  restartSession() {
    this.notebookDataService.initSession(this.notebookToken, false);
  }

  toggleFreezeCell(cellToken: string) {
    this.notebookDataService.toggleFreezeCell(cellToken);
  }

  toggleFoldCell(cellToken: string) {
    this.notebookDataService.toggleFoldCell(cellToken);
  }

  addToLayout(cellToken: string) {
    this.notebookDataService.addCellToLayout(cellToken);
  }

  removeFromLayout(cellToken: string) {
    this.notebookDataService.removeCellFromLayout(cellToken);
  }

  insertCellAbove(cellToken: string) {
    this.notebookDataService.addCell(this.notebookToken, cellToken);
  }

  insertCellBelow(nextCellToken: string) {
    this.notebookDataService.addCell(this.notebookToken, nextCellToken);
  }

  moveCellUp(cellToken: string) {
    this.notebookDataService.moveCell(cellToken, false);
  }

  moveCellDown(cellToken: string) {
    this.notebookDataService.moveCell(cellToken, true);
  }

  // TODO: update to support R
  updateContentType(cellToken: string) {
    this.notebookDataService.updateContentType(
      cellToken,
      this.cellType === 'code' ? NotebookTypes.ContentType.Python : NotebookTypes.ContentType.Markdown
    );
  }

  addRemoveFromLayout(params: { cellToken: string; inLayout: boolean }) {
    const { cellToken, inLayout } = params;
    if (!cellToken) {
      return;
    }

    if (inLayout) {
      this.removeFromLayout(cellToken);
    } else {
      this.addToLayout(cellToken);
    }
  }

  runOrCancelNotebook() {
    if (this.isRunning) {
      this.notebookDataService.cancelRun(this.notebookToken);
    } else {
      if (this.canRun) {
        this.notebookDataService.runNotebook(this.notebookToken);
      }
    }
  }

  clearCellOutputs() {
    this.notebookDataService.clearAllCellOutputs(this.notebookToken);
  }
}

export const notebookToolbarComponent: angular.IComponentOptions = {
  bindings: {
    notebookToken: '<',
    session: '<',
  },
  controller: NotebookToolbarController,
  controllerAs: 'ctrl',
  template: `
<div class="viz-toolbar">
  <div class="button-group">
    <!-- Run All/Cancel/Stop -->
    <capra-tooltip
      [tooltip-content]="ctrl.isCancelling ? 'Stopping' : (ctrl.isRunning ? 'Stop': 'Run all')"
      tooltip-placement="bottom"
      tooltip-delay="500"
      tooltip-size="mini">
      <capra-button class="btn-run-all capra-button"
        ng-class="{
          'running': ctrl.runAllIcon === 'icon-stop',
          'ready': ctrl.runAllIcon === 'icon-play'
        }"
        variant="secondary"
        color="neutral"
        [icon]="ctrl.runAllIcon"
        size="small"
        [text]="ctrl.isCancelling ? 'Stopping...' : (ctrl.isRunning ? 'Stop': 'Run all')"
        [disabled]="!ctrl.isRunning && !ctrl.canRun"
        (clicked)="ctrl.runOrCancelNotebook()">
      </capra-button>
    </capra-tooltip>
    <!-- Restart Session -->
    <capra-tooltip
      tooltip-content="Restart Session"
      tooltip-placement="bottom"
      tooltip-size="mini"
      tooltip-delay="500"
      [tooltip-is-disabled]="!(ctrl.canRestartSession$ | async:this)">
      <capra-button class="capra-button"
        variant="secondary"
        color="neutral"
        [disabled]="!(ctrl.canRestartSession$ | async:this)"
        size="small"
        text="Restart"
        (clicked)="(ctrl.canRestartSession$ | async:this) && ctrl.restartSession()">
      </capra-button>
    </capra-tooltip>
  </div>

  <div class="button-group">
    <!-- Run Cell -->
    <mode-notebook-run-cell-button
      [cell-token]="ctrl.cellToken$ | async:this"
      [next-cell-token]="ctrl.nextCellToken$ | async:this"
      [can-run]="(ctrl.canRunCell$ | async:this) && !(ctrl.isSelectedCellExporting$ | async:this)"
      (run-cell)="ctrl.runCell($event)">
    </mode-notebook-run-cell-button>

    <!-- Insert New Cell -->
    <mode-notebook-add-cell-button
      [cell-token]="ctrl.cellToken$ | async:this"
      [next-cell-token]="ctrl.nextCellToken$ | async:this"
      (insert-cell-above)="ctrl.insertCellAbove($event)"
      (insert-cell-below)="ctrl.insertCellBelow($event)"
      >
    </mode-notebook-add-cell-button>
    <!-- Add/Remove from layout -->
    <mode-notebook-included-report-cell-button
      ng-if="!ctrl.thoughtspotIntegrated && !(ctrl.enableNotebookExportDatasets$ | async:this)"
      [cell-token]="ctrl.cellToken$ | async:this"
      [disabled]="!(ctrl.hasResult$ | async:this)"
      [state]="ctrl.isInLayout$ | async:this"
      (toggle)="ctrl.addRemoveFromLayout($event)">
    </mode-notebook-included-report-cell-button>

    <!-- Use Output Button -->
    <mode-notebook-use-output-menu
      ng-if="(ctrl.enableNotebookExportDatasets$ | async:this)"
      [cell-token]="ctrl.cellToken$ | async:this"
      [disabled]="!(ctrl.hasResult$ | async:this) || (ctrl.isExportInProgress$ | async:this)"
      [is-output-used]="ctrl.isInLayout$ | async:this"
      (toggle)="ctrl.addRemoveFromLayout($event)">
    </mode-notebook-use-output-menu>
  </div>

  <div class="button-group">
    <!-- More Dropdown -->
    <mode-notebook-toolbar-more-dropdown
      [export-link]="ctrl.notebookContentLink$ | async:this"
      [disable-hide-output]="(ctrl.hasOutput$ | async:this) === false"
      (hide-output)="ctrl.clearCellOutputs()">
    </mode-notebook-toolbar-more-dropdown>
  </div>
</div>

  `,
};
